/**
 * Clés de query centralisées pour React Query.
 *
 * Convention hiérarchique :
 *   queryKeys.clubs.all()          → ['clubs']           (invalide TOUT ce qui concerne clubs)
 *   queryKeys.clubs.list(params)   → ['clubs', 'list', params]
 *   queryKeys.clubs.detail(id)     → ['clubs', id]
 *
 * Usage invalidation après mutation :
 *   queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all() })
 */
export const queryKeys = {
  me: () => ['me'] as const,

  users: {
    all: () => ['users'] as const,
    list: (params?: Record<string, unknown>) => ['users', 'list', params] as const,
    detail: (id: number) => ['users', id] as const,
  },

  organizations: {
    all: () => ['organizations'] as const,
    list: (params?: Record<string, unknown>) => ['organizations', 'list', params] as const,
    detail: (id: number) => ['organizations', id] as const,
    adminRequests: (params?: Record<string, unknown>) => ['organizations', 'admin-requests', params] as const,
  },

  clubs: {
    all: () => ['clubs'] as const,
    list: (params?: Record<string, unknown>) => ['clubs', 'list', params] as const,
    detail: (id: number) => ['clubs', id] as const,
    membershipRequests: (clubId: number, params?: Record<string, unknown>) =>
      ['clubs', clubId, 'membership-requests', params] as const,
    myMembershipRequest: () => ['clubs', 'membership-requests', 'my'] as const,
    pendingClaims: () => ['clubs', 'claims', 'pending'] as const,
  },

  competitions: {
    all: () => ['competitions'] as const,
    list: (params?: Record<string, unknown>) => ['competitions', 'list', params] as const,
    detail: (id: number) => ['competitions', id] as const,
    rules: (id: number) => ['competitions', id, 'rules'] as const,
  },

  teams: {
    all: () => ['teams'] as const,
    list: (params?: Record<string, unknown>) => ['teams', 'list', params] as const,
    detail: (id: number) => ['teams', id] as const,
    byCompetition: (competitionId: number) => ['teams', 'competition', competitionId] as const,
  },

  notifications: {
    all: () => ['notifications'] as const,
    count: () => ['notifications', 'count'] as const,
    list: (params?: Record<string, unknown>) => ['notifications', 'list', params] as const,
    preferences: () => ['notifications', 'preferences'] as const,
  },

  payments: {
    all: () => ['payments'] as const,
    list: (params?: Record<string, unknown>) => ['payments', 'list', params] as const,
    detail: (id: number) => ['payments', id] as const,
  },

  mfa: {
    status: () => ['mfa', 'status'] as const,
    trustedDevices: () => ['mfa', 'trusted-devices'] as const,
  },

  saasParams: () => ['saasParams'] as const,

  auditLogs: (params: { page: number; limit: number; action?: string }) => ['admin', 'audit-logs', params] as const,

  ffpjp: {
    checkLicense: (licenseNumber: string) => ['ffpjp', 'licenses', licenseNumber, 'check'] as const,
    syncRuns: () => ['ffpjp', 'sync', 'runs'] as const,
  },

  blog: {
    all: () => ['blog'] as const,
    articles: (params?: Record<string, unknown>) => ['blog', 'articles', params] as const,
    article: (id: number) => ['blog', 'articles', id] as const,
    // Lot A bis : la clé DOIT porter le scope. Sans lui, le blog d'un club et
    // celui de la plateforme partageraient la même entrée de cache — un admin
    // multi-casquettes verrait la taxonomie d'une entité sur une autre.
    categories: (scope?: { clubId?: number; organizationId?: number }) =>
      ['blog', 'categories', scope ?? null] as const,
  },
};
